// theme/ThemeProvider.tsx
import { AppTheme, createNavigationTheme, createTheme } from '@/constants/theme';
import { ThemePreference, useThemePreference } from '@/hooks/use-theme-preference';
import { ThemeProvider as NavigationThemeProvider } from 'expo-router';
import { useColorScheme as useNativeWindColorScheme } from 'nativewind';
import React, { createContext, useContext } from 'react';

interface ThemeContextValue {
  theme: AppTheme;
  isDarkMode: boolean;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => Promise<void>;
  toggleDarkMode: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function UnifiedThemeProvider({ children }: { children: React.ReactNode }) {
  const {
    isDarkMode,
    preference,
    setPreference,
    toggleDarkMode,
  } = useThemePreference();

  // TEMPORARY: Force dark mode for v1 launch.
  // Revert to preference-based resolution when theme selection UI ships.
  const resolvedScheme = 'dark';

  // Sync NativeWind's CSS variable color scheme with our forced scheme
  const { setColorScheme } = useNativeWindColorScheme();
  setColorScheme(resolvedScheme);

  const theme = createTheme(resolvedScheme);
  const navigationTheme = createNavigationTheme(resolvedScheme);

  const contextValue: ThemeContextValue = {
    theme,
    isDarkMode,
    preference,
    setPreference,
    toggleDarkMode,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <NavigationThemeProvider value={navigationTheme}>
        {children}
      </NavigationThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useAppTheme(): AppTheme {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within UnifiedThemeProvider');
  }
  return context.theme;
}

export function useThemeControl() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeControl must be used within UnifiedThemeProvider');
  }
  return {
    isDarkMode: context.isDarkMode,
    preference: context.preference,
    setPreference: context.setPreference,
    toggleDarkMode: context.toggleDarkMode,
  };
}
